import type { Repositories } from '@core/db';
import type { FileStore } from '@core/files';
import type { TelemetrySnapshot } from './events';
import { telemetry, type LocalTelemetry } from './store';
import { uploadStats } from './uploader';

/**
 * Суточная задача веб-версии: снять срез состояния, выбросить просроченное,
 * отправить накопленное. Порт `DailyStatsWorker` из Android.
 *
 * **Расписания здесь нет и быть не может.** `WorkManager` в браузере не существует,
 * а `Periodic Background Sync` в Safari не поддерживается вовсе — единственный
 * надёжный момент, когда наш код выполняется, это открытая страница. Поэтому задача
 * запускается при запуске приложения и при возвращении к нему, но не чаще раза в час.
 *
 * Потери от этого нет: очередь хранит 30 суток, сервер идемпотентен по
 * `(install_id, дата, метрика)`, и то, что не ушло сегодня, уйдёт при следующем
 * открытии — не задвоившись.
 */
const RUN_EVERY_MS = 60 * 60 * 1000;

const BYTES_IN_MB = 1024 * 1024;

export async function runDailyStats(
  db: Repositories,
  files: FileStore,
  version: string,
  store: LocalTelemetry = telemetry,
  now: number = Date.now(),
): Promise<boolean> {
  if (now - store.lastRunAt() < RUN_EVERY_MS) return false;

  return sendStatsNow(db, files, version, store, now);
}

/**
 * То же самое, но немедленно — по кнопке в настройках.
 *
 * Нужна, чтобы отправку можно было проверить на живом устройстве, где нет ни отладчика,
 * ни возможности подождать час: юрист нажимает и тут же видит, дошло или нет. Пилот
 * начинается с вопроса «а работает ли вообще», и ответ на него должен быть в приложении,
 * а не в нашей переписке.
 */
export async function sendStatsNow(
  db: Repositories,
  files: FileStore,
  version: string,
  store: LocalTelemetry = telemetry,
  now: number = Date.now(),
): Promise<boolean> {
  store.ran();

  // Срез и уборка делаются и при выключенной отправке: это порядок на устройстве,
  // а не про сервер. Наружу без согласия по-прежнему не уходит ничего — это решает
  // сам отправитель.
  store.saveSnapshot(await snapshot(db, files, now));
  store.dropOlderThan();

  return uploadStats(store, version);
}

/** Срез «сколько ведёт» — те же семь чисел, что считает Android. */
export async function snapshot(
  db: Repositories,
  files: FileStore,
  now: number,
): Promise<TelemetrySnapshot> {
  const cases = await db.cases.list({});
  const events = await db.events.all();
  const clients = await db.clients.list();

  const tasks = events.filter((event) => event.type === 'Task' && !event.isCompleted);

  const documents = await Promise.all(cases.map((item) => db.documents.ofCase(item.id)));

  return {
    cases_active: cases.filter((item) => item.status === 'Active').length,
    cases_total: cases.length,
    documents_total: documents.reduce((sum, list) => sum + list.length, 0),
    tasks_open: tasks.length,
    // Срок задачи — тот же, по которому её считает просроченной экран задач.
    tasks_overdue: tasks.filter((task) => (task.deadlineAt ?? task.startAt) < now).length,
    clients_total: clients.length,
    storage_mb: Math.round((await files.totalSizeBytes()) / BYTES_IN_MB),
  };
}
